import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { parsePhoneNumber, formatPhoneNumberForDisplay } from '../utils/phoneNumberParser';
import { isValidStateAbbreviation } from '../utils/stateValidator';
import { Container, Row, Col, Button, Form, Table, Alert, Card, Badge } from 'react-bootstrap';
import { DeleteConfirmModal } from './shared/DeleteConfirmModal';
import addIcon from '../assets/icons/add.svg';
import closeIcon from '../assets/icons/close.svg';
import { usePaginatedData } from '../hooks/usePaginatedData';
import { useFormData } from '../hooks/useFormData';
import { useStores } from '../hooks/useStores';
import { useURLParams } from '../hooks/useURLParams';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { SearchableSelect } from './shared/SearchableSelect';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone', className: 'd-none d-md-table-cell' },
  { key: 'insurance_company', label: 'Insurance Co.' },
  { key: 'insurance_policy', label: 'Policy #' },
  { key: 'status', label: 'Status' },
  { key: 'payment_activity', label: 'Last 4 Weeks' },
  { key: 'last_payment', label: 'Last Payment' },
  { key: 'actions', label: 'Actions', className: 'd-none d-md-table-cell' },
];

const formatDateOnly = (value) => {
  if (!value) return 'Never';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export function WeeklyPaymentActivity({ weeks = [] }) {
  if (weeks.length === 0) return <span className="text-muted">No data</span>;

  return (
    <div className="d-flex gap-1 text-nowrap" aria-label="Weekly payment activity">
      {weeks.map((week) => {
        const label = `${formatDateOnly(week.week_start)}: ${week.paid ? 'payment received' : 'no payment'}`;
        return (
          <span
            key={week.week_start}
            role="img"
            aria-label={label}
            title={label}
            style={{
              color: week.paid ? '#198754' : '#dc3545',
              fontSize: '1.35rem',
              lineHeight: 1,
            }}
          >
            ●
          </span>
        );
      })}
    </div>
  );
}

/**
 * CustomerList component displays all customers in a table
 * and provides navigation to detail view and creation
 */
export function CustomerList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall, user } = useOutletContext();
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renewingTransactionId, setRenewingTransactionId] = useState(null);
  const [renewError, setRenewError] = useState(null);
  const [renewMessage, setRenewMessage] = useState(null);

  // URL-synced filters and pagination
  const { getParam, setParam, setParams } = useURLParams({ store_id: '', page: 0 });
  const selectedStore = getParam('store_id');
  const urlPage = getParam('page');

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  // Use stores hook
  const { stores } = useStores(apiCall);

  // Smart search handler for customer lookup — no store filter so all customers are searchable
  const handleCustomerSearch = useCallback(async (term) => {
    const params = new URLSearchParams({ search: term, limit: '20' });
    const res = await apiCall(`${API_BASE_URL}/customers?${params}`);
    return res?.data || [];
  }, [apiCall]);

  // Navigate to customer detail on selection
  const handleSearchSelect = useCallback((customerId) => {
    if (customerId) {
      navigate(`./${customerId}`);
    }
  }, [navigate]);

  // Use paginated data hook with store filter
  const {
    data: filteredCustomers,
    loading,
    error: loadError,
    pagination,
    reload,
    goToNextPage: _goToNextPage,
    goToPreviousPage: _goToPreviousPage,
  } = usePaginatedData(
    async (queryParams) => apiCall(`${API_BASE_URL}/customers?${queryParams}`),
    { limit: 20, filters: { store_id: selectedStore }, dependencies: [apiCall], initialOffset: urlPage * 20 }
  );

  // Wrap pagination to sync URL
  const goToNextPage = useCallback(() => {
    _goToNextPage();
    const nextPage = Math.floor(pagination.offset / pagination.limit) + 1;
    setParam('page', nextPage, { replace: true });
  }, [_goToNextPage, pagination, setParam]);

  const goToPreviousPage = useCallback(() => {
    _goToPreviousPage();
    const prevPage = Math.floor(pagination.offset / pagination.limit) - 1;
    setParam('page', prevPage > 0 ? prevPage : '', { replace: true });
  }, [_goToPreviousPage, pagination, setParam]);

  // Use form data hook
  const {
    formData,
    setFormData,
    handleChange,
    resetForm,
  } = useFormData({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    driver_license_number: '',
    driver_license_state: 'IN',
    international_license: false,
    referral: '',
    sms_consent: false,
  }, {
    typeMap: { international_license: 'boolean', sms_consent: 'boolean' }
  });

  // Custom input handler for state abbreviation formatting
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Limit driver_license_state to 2 characters (US state abbreviations)
    if (name === 'driver_license_state') {
      const processedValue = value.slice(0, 2).toUpperCase();
      setFormData(prev => ({ ...prev, [name]: processedValue }));
    } else {
      handleChange(e);
    }
  };

  // Auto-open form if navigated from Quick Actions
  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowNewCustomerForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validate and parse phone number
    let parsedPhone;
    try {
      parsedPhone = parsePhoneNumber(formData.phone_number);
    } catch (error) {
      setFormError(error.message);
      return;
    }

    // Validate state abbreviation
    if (!isValidStateAbbreviation(formData.driver_license_state)) {
      setFormError('Please enter a valid US state abbreviation (2 characters)');
      return;
    }

    try {
      const { sms_consent, ...customerFields } = { ...formData, phone_number: parsedPhone };

      const response = await apiCall(
        `${API_BASE_URL}/customers`,
        {
          method: 'POST',
          body: JSON.stringify(customerFields),
        }
      );

      if (response.success || response) {
        const newCustomer = response.data || response;

        // Record SMS consent if checked
        if (sms_consent && newCustomer.id) {
          try {
            await apiCall(`${API_BASE_URL}/customers/${newCustomer.id}/consent`, {
              method: 'POST',
              body: JSON.stringify({ consent_given: true }),
            });
          } catch (consentErr) {
            console.error('Failed to record SMS consent:', consentErr);
          }
        }

        resetForm();
        setShowNewCustomerForm(false);
        alert('Customer added successfully');
        reload();
      } else {
        setFormError(response.error || 'Failed to add customer');
      }
    } catch (error) {
      setFormError(error.message || 'Error adding customer');
    }
  };

  const handleDeleteCustomer = (customerId) => {
    setDeleteTargetId(customerId);
    setDeleteError(null);
  };

  const confirmDeleteCustomer = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiCall(`${API_BASE_URL}/customers/${deleteTargetId}`, { method: 'DELETE' });
      setDeleteTargetId(null);
      reload();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRenew = async (candidate) => {
    setRenewingTransactionId(candidate.transaction_id);
    setRenewError(null);
    setRenewMessage(null);
    try {
      const response = await apiCall(
        `${API_BASE_URL}/transactions/${candidate.transaction_id}/renew`,
        { method: 'POST' }
      );
      const renewedId = response?.data?.id;
      setRenewMessage(
        `${candidate.vehicle_label} renewed${renewedId ? ` as transaction #${renewedId}` : ''}.`
      );
      reload();
    } catch (error) {
      setRenewError(error.message || 'Unable to renew this rental');
    } finally {
      setRenewingTransactionId(null);
    }
  };

  const renderCell = (key, customer) => {
    switch (key) {
      case 'id':
        return (
          <span role="button" className="text-primary" style={{cursor:'pointer'}} onClick={() => navigate(`./${customer.id}`)}>
            {customer.id}
          </span>
        );
      case 'name':
        return (
          <span role="button" className="text-primary" style={{cursor:'pointer'}} onClick={() => navigate(`./${customer.id}`)}>
            {customer.first_name} {customer.last_name}
          </span>
        );
      case 'phone':
        return formatPhoneNumberForDisplay(customer.phone_number);
      case 'status':
        return (
          <>
            <Badge bg={customer.wallet_status === 'past_due' ? 'danger' : 'success'}>
              {customer.wallet_status === 'past_due' ? 'Past Due' : 'Current'}
            </Badge>
            {customer.wallet_status === 'past_due' && customer.current_balance > 0 && (
              <small className="d-block text-muted">${customer.current_balance.toFixed(2)}</small>
            )}
          </>
        );
      case 'payment_activity':
        return <WeeklyPaymentActivity weeks={customer.weekly_payment_activity || []} />;
      case 'last_payment':
        return formatDateOnly(customer.last_payment_date);
      case 'insurance_company':
        return customer.insurance_company || '-';
      case 'insurance_policy':
        return customer.insurance_policy_number || '-';
      case 'actions':
        return (
          <div className="d-flex flex-column align-items-start gap-1">
            {user?.role !== 'auditor' && (customer.renewal_candidates || []).map((candidate) => (
              <div key={candidate.transaction_id} className="d-flex align-items-center gap-1">
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => handleRenew(candidate)}
                  disabled={renewingTransactionId !== null}
                  aria-label={`Renew ${candidate.vehicle_label}`}
                >
                  {renewingTransactionId === candidate.transaction_id ? 'Renewing…' : 'Renew'}
                </Button>
                <small className="text-muted" title={candidate.vehicle_label}>
                  {candidate.vehicle_label}
                </small>
              </div>
            ))}
            <Button variant="link" size="sm" onClick={() => navigate(`./${customer.id}`)} className="me-2">
              View
            </Button>
            {user?.role !== 'auditor' && (
              <Button variant="link" size="sm" className="text-danger" onClick={() => handleDeleteCustomer(customer.id)}>
                Delete
              </Button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Container fluid className="py-4 domain-customers">
      <Row className="mb-4">
        <Col>
          <h2>Customers</h2>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6} lg={5}>
          <SearchableSelect
            onSearch={handleCustomerSearch}
            onSelectItem={handleSearchSelect}
            getItemLabel={(c) => `${c.first_name} ${c.last_name}`}
            renderItem={(c) => (
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontWeight: 500 }}>{c.first_name} {c.last_name}</div>
                  <small style={{ color: '#6c757d' }}>
                    {c.phone_number ? formatPhoneNumberForDisplay(c.phone_number) : 'No phone'}
                  </small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Badge bg={c.wallet_status === 'past_due' ? 'danger' : 'success'} style={{ fontSize: '0.75rem' }}>
                    ${(c.current_balance || 0).toFixed(2)}
                  </Badge>
                </div>
              </div>
            )}
            placeholder="Search customers by name, phone, email, or ID..."
            required={false}
            className="mb-0"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          {user?.role !== 'auditor' && (
            <Button
              variant={showNewCustomerForm ? "secondary" : "primary"}
              onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
            >
              {showNewCustomerForm ? <><img src={closeIcon} alt="" className="icon-btn" />Cancel</> : <><img src={addIcon} alt="" className="icon-btn" />Add New Customer</>}
            </Button>
          )}
        </Col>
        <Col md={8} className="d-flex align-items-center justify-content-end gap-3">
          {stores.length > 1 && (
            <>
              <Form.Label className="mb-0 fw-bold">Filter by Store:</Form.Label>
              <Form.Select
                value={selectedStore}
                onChange={(e) => setParams({ store_id: e.target.value, page: '' })}
                style={{ maxWidth: '250px' }}
              >
                <option value="">All Stores</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </Form.Select>
              {selectedStore && (
                <Badge bg="secondary">
                  {filteredCustomers.length} customers
                </Badge>
              )}
            </>
          )}
          <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
        </Col>
      </Row>

      {renewMessage && (
        <Alert variant="success" dismissible onClose={() => setRenewMessage(null)}>
          {renewMessage}
        </Alert>
      )}
      {renewError && (
        <Alert variant="danger" dismissible onClose={() => setRenewError(null)}>
          {renewError}
        </Alert>
      )}

      {showNewCustomerForm && (
        <Card className="mb-4">
          <Card.Header className="text-white" style={{ backgroundColor: '#6366f1' }}>
            <h3>Add New Customer</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddCustomer}>
              {formError && <Alert variant="danger">{formError}</Alert>}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Driver License Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_license_number"
                      value={formData.driver_license_number}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Driver License State</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_license_state"
                      value={formData.driver_license_state}
                      onChange={handleInputChange}
                      required
                      maxLength="2"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>International License</Form.Label>
                    <Form.Select
                      name="international_license"
                      value={formData.international_license ? 'yes' : 'no'}
                      onChange={handleInputChange}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Referral</Form.Label>
                    <Form.Control
                      type="text"
                      name="referral"
                      value={formData.referral}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-3" />
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="sms_consent"
                  name="sms_consent"
                  label="Customer consents to receive SMS notifications"
                  checked={formData.sms_consent}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  Message frequency varies. Message and data rates may apply.
                </Form.Text>
              </Form.Group>

              <Button type="submit" variant="primary">
                Save Customer
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          {filteredCustomers.length > 0 && (
            <div className="px-3 py-2 bg-light d-flex flex-wrap justify-content-between gap-2">
              <small className="text-muted">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} customers
              </small>
              <small className="text-muted" aria-label="Payment activity legend">
                Oldest → newest:&nbsp;
                <span style={{ color: '#198754' }}>●</span> Payment&nbsp;&nbsp;
                <span style={{ color: '#dc3545' }}>●</span> No payment
              </small>
            </div>
          )}

          <Table striped hover responsive>
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key} className={col.className}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map(customer => {
                  const threeMonthsAgo = new Date();
                  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                  const isInactive = !customer.last_activity_at || new Date(customer.last_activity_at) < threeMonthsAgo;
                  return (
                  <tr key={customer.id} style={isInactive ? { opacity: 0.5, backgroundColor: '#f0f0f0' } : {}}>
                    {visibleColumns.map(col => (
                      <td key={col.key} className={col.className}>{renderCell(col.key, customer)}</td>
                    ))}
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4">
                    {selectedStore ? 'No customers found at this store' : 'No customers found'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {filteredCustomers.length > 0 && pagination.total > pagination.limit && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button
            variant="secondary"
            onClick={goToPreviousPage}
            disabled={pagination.offset === 0}
          >
            &larr; Previous
          </Button>
          <span className="text-muted">
            Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            onClick={goToNextPage}
            disabled={pagination.offset + pagination.limit >= pagination.total}
          >
            Next &rarr;
          </Button>
        </div>
      )}

      <DeleteConfirmModal
        show={!!deleteTargetId}
        onHide={() => setDeleteTargetId(null)}
        onConfirm={confirmDeleteCustomer}
        itemLabel="customer"
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Container>
  );
}

export default CustomerList;
